import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Clock, GitBranch, User, FileText, X, RotateCcw, ChevronRight } from 'lucide-react';
import WorkflowService from '../../services/workflow.service';
import { useOrganization } from '../../contexts/OrganizationContext';
import ConfirmDialog from '../ui/ConfirmDialog';

export default function WorkflowVersions({ workflowId, projectId, currentVersion, onClose, onVersionChange }) {
  const { currentAccount } = useOrganization();
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [rolling, setRolling] = useState(false);
  const [confirmRollback, setConfirmRollback] = useState({ open: false, versionNumber: null });

  useEffect(() => {
    if (workflowId) {
      loadVersions();
    }
  }, [workflowId]);

  const loadVersions = async () => {
    setLoading(true);
    setError(null);

    try {
      const result = await WorkflowService.listWorkflowVersions(workflowId, { limit: 50 });
      
      if (result.success) {
        setVersions(result.data.versions || []);
      } else {
        setError(result.error || 'Failed to load versions');
      }
    } catch (err) {
      setError(err.message || 'Failed to load versions');
    } finally {
      setLoading(false);
    }
  };

  const handleRollback = async (versionNumber) => {
    setConfirmRollback({ open: true, versionNumber });
  };

  const executeRollback = async (versionNumber) => {
    setConfirmRollback({ open: false, versionNumber: null });
    setRolling(true);
    setError(null);

    try {
      const result = await WorkflowService.rollbackWorkflow(workflowId, {
        account_id: currentAccount?.id,
        project_id: projectId,
        target_version: versionNumber,
        reason: 'Manual rollback from UI'
      });

      if (result.success) {
        await loadVersions();
        if (onVersionChange) {
          onVersionChange(result.data.workflow);
        }
      } else {
        setError(result.error || 'Failed to rollback');
      }
    } catch (err) {
      setError(err.message || 'Failed to rollback');
    } finally {
      setRolling(false);
    }
  };

  const viewVersionDetails = async (versionNumber) => {
    try {
      const result = await WorkflowService.getWorkflowVersion(workflowId, versionNumber);
      
      if (result.success) {
        setSelectedVersion(result.data);
      } else {
        setError(result.error || 'Failed to load version details');
      }
    } catch (err) {
      setError(err.message || 'Failed to load version details');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-zinc-900 border border-white/10 rounded-xl shadow-2xl w-full max-w-3xl max-h-[80vh] flex flex-col"
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-white/10 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-violet-500/10 flex items-center justify-center">
              <GitBranch className="w-5 h-5 text-violet-400" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-white">Version History</h2>
              <p className="text-xs text-gray-400">View and rollback to previous versions</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors p-2 hover:bg-white/5 rounded-lg"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Error */}
        {error && (
          <div className="mx-6 mt-4 px-4 py-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
            {error}
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="w-8 h-8 border-2 border-violet-500 border-t-transparent rounded-full animate-spin mx-auto mb-2" />
                <p className="text-sm text-gray-400">Loading versions...</p>
              </div>
            </div>
          ) : versions.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <div className="w-16 h-16 rounded-full bg-gray-800/50 flex items-center justify-center mb-4">
                <GitBranch className="w-8 h-8 text-gray-600" />
              </div>
              <h3 className="text-sm font-medium text-gray-400 mb-1">No Versions Yet</h3>
              <p className="text-xs text-gray-500">Publish your workflow to create the first version</p>
            </div>
          ) : (
            <div className="space-y-3">
              {versions.map((version, index) => (
                <motion.div
                  key={version.id || index}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className={`p-4 rounded-lg border transition-all ${
                    version.version_number === currentVersion
                      ? 'bg-violet-500/10 border-violet-500/30'
                      : 'bg-white/5 border-white/10 hover:border-white/20'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-sm font-semibold text-white">
                          Version {version.version_number}
                        </span>
                        {version.version_number === currentVersion && (
                          <span className="px-2 py-0.5 text-xs font-medium bg-violet-500/20 text-violet-300 rounded">
                            Current
                          </span>
                        )}
                      </div>

                      <div className="space-y-1.5 text-xs text-gray-400">
                        <div className="flex items-center gap-2">
                          <Clock className="w-3.5 h-3.5" />
                          <span>{formatDate(version.published_at)}</span>
                        </div>

                        {version.notes && (
                          <div className="flex items-start gap-2">
                            <FileText className="w-3.5 h-3.5 mt-0.5" />
                            <span className="text-gray-300">{version.notes}</span>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => viewVersionDetails(version.version_number)}
                        className="px-3 py-1.5 text-xs text-gray-400 hover:text-white border border-white/10 hover:border-white/20 rounded-lg transition-all"
                      >
                        <ChevronRight className="w-4 h-4" />
                      </button>

                      {version.version_number !== currentVersion && (
                        <button
                          onClick={() => handleRollback(version.version_number)}
                          disabled={rolling || confirmRollback.open}
                          className="px-3 py-1.5 text-xs font-medium text-white bg-primary hover:bg-violet-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-all flex items-center gap-1.5"
                        >
                          <RotateCcw className="w-3.5 h-3.5" />
                          Rollback
                        </button>
                      )}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>

        {/* Version Details Modal */}
        <AnimatePresence>
          {selectedVersion && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center p-6"
              onClick={() => setSelectedVersion(null)}
            >
              <motion.div
                initial={{ scale: 0.95 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0.95 }}
                onClick={(e) => e.stopPropagation()}
                className="bg-zinc-900 border border-white/10 rounded-xl max-w-2xl w-full max-h-[70vh] overflow-hidden"
              >
                <div className="px-6 py-4 border-b border-white/10 flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-white">
                    Version {selectedVersion.version_number} Details
                  </h3>
                  <button
                    onClick={() => setSelectedVersion(null)}
                    className="text-gray-400 hover:text-white transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <div className="p-6 overflow-y-auto max-h-[calc(70vh-80px)]">
                  <div className="space-y-4">
                    <div>
                      <label className="text-xs font-medium text-gray-400 uppercase tracking-wider">
                        Published At
                      </label>
                      <p className="mt-1 text-sm text-white">{formatDate(selectedVersion.published_at)}</p>
                    </div>

                    {selectedVersion.notes && (
                      <div>
                        <label className="text-xs font-medium text-gray-400 uppercase tracking-wider">
                          Notes
                        </label>
                        <p className="mt-1 text-sm text-white">{selectedVersion.notes}</p>
                      </div>
                    )}

                    {selectedVersion.snapshot && (
                      <div>
                        <label className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-2 block">
                          Snapshot
                        </label>
                        <pre className="text-xs text-gray-300 bg-black/40 p-4 rounded-lg overflow-x-auto">
                          {JSON.stringify(selectedVersion.snapshot, null, 2)}
                        </pre>
                      </div>
                    )}
                  </div>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>

      <ConfirmDialog
        isOpen={confirmRollback.open}
        title="Rollback Workflow"
        message={`Roll back to version ${confirmRollback.versionNumber}? This will create a new version with the same content.`}
        confirmText="Rollback"
        cancelText="Cancel"
        variant="warning"
        onConfirm={() => executeRollback(confirmRollback.versionNumber)}
        onCancel={() => setConfirmRollback({ open: false, versionNumber: null })}
      />
    </motion.div>
  );
}
